import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

import logo from "../assets/logo.png";
import sideImage from "../assets/login-side.png";

export default function Login() {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 768
  );

  const navigate = useNavigate();

  // ==========================
  // 📱 MOBILE DETECTION
  // ==========================
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, []);

  // ==========================
  // 🔐 LOGIN
  // ==========================
  const login = async () => {
    try {
      if (!mobile || !password) {
        return alert(
          "Enter mobile & password"
        );
      }

      setLoading(true);

      const res = await API.post("/login", {
        mobile: mobile.trim(),
        password
      });

      const token = res.data.token;

      const user = {
        ...res.data.user,
        role: String(
          res.data.user.role || ""
        )
          .toLowerCase()
          .trim()
      };

      localStorage.setItem(
        "token",
        token
      );

      localStorage.setItem(
        "user",
        JSON.stringify(user)
      );

      // ==========================
      // 🔀 ROLE ROUTES
      // ==========================
      switch (user.role) {
        case "salesman":
          navigate("/sales");
          break;

        case "finance":
          navigate("/payouts");
          break;

        case "manager":
          navigate("/manager");
          break;

        case "ceo":
          navigate("/ceo/claims");
          break;

        default:
          navigate("/");
      }

    } catch (err) {
      console.log(err);

      alert(
        err.response?.data?.msg ||
        "Login failed"
      );

    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>

      {/* BACKGROUND GLOW */}
      <div style={styles.glow1}></div>
      <div style={styles.glow2}></div>

      {/* MAIN CONTAINER */}
      <div
        style={{
          ...styles.container,
          flexDirection: isMobile
            ? "column"
            : "row"
        }}
      >

        {/* LEFT PANEL */}
        {!isMobile && (
          <div style={styles.left}>

            <div style={styles.leftContent}>

              <img
                src={sideImage}
                alt="Login Illustration"
                style={styles.sideImage}
              />

              <h1 style={styles.leftTitle}>
                Sales Incentive
                Management System
              </h1>

              <p style={styles.leftText}>
                Manage claims, approvals,
                payouts and reports in
                one centralized platform.
              </p>

            </div>

          </div>
        )}

        {/* RIGHT PANEL */}
        <div
          style={{
            ...styles.right,
            width: isMobile
              ? "100%"
              : "45%"
          }}
        >

          {/* LOGO */}
          <div style={styles.logoWrapper}>

            <img
              src={logo}
              alt="Canon Logo"
              style={{
                ...styles.logo,
                width: isMobile
                  ? 180
                  : 240
              }}
            />

          </div>

          {/* LOGIN TITLE */}
          <h2 style={styles.title}>
            Welcome Back
          </h2>

          <p style={styles.subtitle}>
            Sign in to continue
          </p>

          {/* MOBILE */}
          <div style={styles.inputGroup}>

            <label style={styles.label}>
              Mobile Number
            </label>

            <input
              placeholder="923XXXXXXXXX"
              value={mobile}
              onChange={(e) =>
                setMobile(
                  e.target.value.replace(
                    /[^0-9]/g,
                    ""
                  )
                )
              }
              style={styles.input}
            />

          </div>

          {/* PASSWORD */}
          <div style={styles.inputGroup}>

            <label style={styles.label}>
              Password
            </label>

            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) =>
                setPassword(
                  e.target.value
                )
              }
              style={styles.input}
            />

          </div>

          {/* OPTIONS */}
          <div style={styles.options}>

            <label style={styles.remember}>

              <input type="checkbox" />

              Remember me

            </label>

            <span style={styles.forgot}>
              Forgot Password?
            </span>

          </div>

          {/* LOGIN BUTTON */}
          <button
            onClick={login}
            disabled={loading}
            style={{
              ...styles.button,
              opacity: loading
                ? 0.7
                : 1
            }}
          >
            {loading
              ? "Please wait..."
              : "Log In"}
          </button>

        </div>

      </div>

    </div>
  );
}

// ==========================
// 🎨 STYLES
// ==========================
const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #eef2ff 0%, #f7f9fc 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    overflow: "hidden",
    position: "relative",
    fontFamily: "'Segoe UI', sans-serif"
  },

  // BACKGROUND GLOW
  glow1: {
    position: "absolute",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "rgba(108,99,255,0.15)",
    top: -100,
    left: -100,
    filter: "blur(80px)"
  },

  glow2: {
    position: "absolute",
    width: 350,
    height: 350,
    borderRadius: "50%",
    background:
      "rgba(0,123,255,0.10)",
    bottom: -100,
    right: -100,
    filter: "blur(80px)"
  },

  // MAIN CARD
  container: {
    width: "100%",
    maxWidth: 1180,
    minHeight: 650,
    background:
      "rgba(255,255,255,0.78)",
    backdropFilter: "blur(16px)",
    borderRadius: 30,
    overflow: "hidden",
    display: "flex",
    boxShadow:
      "0 15px 50px rgba(0,0,0,0.08)",
    position: "relative",
    zIndex: 2
  },

  // LEFT PANEL
  left: {
    flex: 1,
    background:
      "linear-gradient(135deg, #6c63ff 0%, #5a54e8 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 60,
    color: "#fff"
  },

  leftContent: {
    maxWidth: 450,
    textAlign: "center"
  },

  sideImage: {
    width: "100%",
    maxWidth: 420,
    marginBottom: 30
  },

  leftTitle: {
    fontSize: 38,
    fontWeight: "700",
    marginBottom: 20,
    lineHeight: 1.3
  },

  leftText: {
    fontSize: 17,
    lineHeight: 1.7,
    opacity: 0.92
  },

  // RIGHT PANEL
  right: {
    background: "#fff",
    padding: "50px 40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },

  logoWrapper: {
    textAlign: "center",
    marginBottom: 10
  },

  logo: {
    objectFit: "contain"
  },

  title: {
    fontSize: 34,
    color: "#2c2c54",
    marginBottom: 10,
    textAlign: "center",
    fontWeight: "700"
  },

  subtitle: {
    color: "#8c8ca1",
    marginBottom: 35,
    textAlign: "center",
    fontSize: 15
  },

  inputGroup: {
    marginBottom: 22
  },

  label: {
    display: "block",
    marginBottom: 10,
    color: "#444",
    fontWeight: "600",
    fontSize: 14
  },

  input: {
    width: "100%",
    padding: 16,
    borderRadius: 14,
    border: "1px solid #e4e6f1",
    background: "#f8f9fd",
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
    transition: "0.3s"
  },

  options: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 30,
    fontSize: 14,
    color: "#666",
    flexWrap: "wrap",
    gap: 10
  },

  remember: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },

  forgot: {
    cursor: "pointer",
    color: "#6c63ff",
    fontWeight: "600"
  },

  button: {
    width: "100%",
    padding: 16,
    borderRadius: 14,
    border: "none",
    background:
      "linear-gradient(135deg, #6c63ff 0%, #5a54e8 100%)",
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    cursor: "pointer",
    boxShadow:
      "0 10px 25px rgba(108,99,255,0.35)",
    transition: "0.3s"
  }
};